import type { KyInstance } from 'ky';
import type {
  PluginCatalogueItem,
  PluginConnectionCheck,
  PluginConnectionKey,
  PluginGrantRequest,
  PluginInstallation,
  PluginMember,
  PluginMemberRequest,
  PluginLinkProof,
  PluginLinkView,
  PluginSyncHealth,
} from './types/plugins';

const installation = (id: string) => `api/v1/plugin-installations/${id}`;
const connectionKey = (pluginId: string) => `api/v1/plugins/${pluginId}/connection-key`;
export function buildPluginApi(http: KyInstance) {
  return {
    connectionKey: (pluginId: string) =>
      http.get(connectionKey(pluginId)).json<PluginConnectionKey>(),
    saveConnectionKey: (pluginId: string, key: string) =>
      http.put(connectionKey(pluginId), { json: { key } }).json<PluginConnectionKey>(),
    removeConnectionKey: (pluginId: string) =>
      http.delete(connectionKey(pluginId)).json<PluginConnectionKey>(),
    checkConnectionKey: (pluginId: string) =>
      http.post(`${connectionKey(pluginId)}/check`).json<PluginConnectionCheck>(),
    list: () => http.get('api/v1/plugins').json<PluginCatalogueItem[]>(),
    install: (id: string, body: PluginGrantRequest) =>
      http.post(`api/v1/plugins/${id}/installations`, { json: body }).json<PluginInstallation>(),
    members: (id: string) => http.get(`${installation(id)}/members`).json<PluginMember[]>(),
    syncHealth: (id: string) => http.get(`${installation(id)}/sync`).json<PluginSyncHealth>(),
    retrySync: (id: string) => http.post(`${installation(id)}/sync/retry`).then(() => undefined),
    previewMember: (id: string, userId: string, body: PluginMemberRequest) =>
      http
        .post(`${installation(id)}/members/${userId}/preview`, { json: body })
        .then(() => undefined),
    setMember: (id: string, userId: string, body: PluginMemberRequest) =>
      http
        .put(`${installation(id)}/members/${userId}`, { json: body })
        .json<{ revision: number }>(),
    changeState: (id: string, action: 'disable' | 'revoke', expectedRevision: number) =>
      http
        .post(`${installation(id)}/${action}`, { json: { expectedRevision } })
        .json<PluginInstallation>(),
    resume: (id: string, expectedRevision: number, grant: PluginGrantRequest) =>
      http
        .post(`${installation(id)}/resume`, { json: { expectedRevision, grant } })
        .json<PluginInstallation>(),
    previewLink: (id: string, proof: PluginLinkProof) =>
      http.post(`api/v1/plugin-links/${id}/preview`, { json: proof }).json<PluginLinkView>(),
    confirmLink: (id: string, proof: PluginLinkProof, installationId: string) =>
      http
        .post(`api/v1/plugin-links/${id}/confirm`, { json: { ...proof, installationId } })
        .then(() => undefined),
  };
}
